import { CheckmarkCircleFilled } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, WoltLogo } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { SearchUser } from 'apis/restaurant-api/merchant-admin/user/types';
import { Typography } from 'common/typography';

import { TestIds } from '../../utils/testUtils';
import CampaignFormItem from '../CampaignFormItem';

export type AgreementsDetailsProps = {
  author?: SearchUser;
  agreements: React.ReactNode[];
  isCreatedByWolt?: boolean;
  isLoading?: boolean;
  'data-test-id'?: string;
};

const StyledCreatorWrapper = styled(Typography.Body3).attrs({
  fontWeight: 'semiBold',
})`
  margin: 0 0 1.14em;
  padding: 0;
  color: ${cbSemanticCssVars.textSecondary};
  display: flex;
  align-items: baseline;
  gap: 0.3em;
  flex-wrap: wrap;
`;

const StyledCreator = styled(Typography.Body3).attrs({
  fontWeight: 'normal',
})`
  color: ${cbSemanticCssVars.textPrimary};
  display: flex;
  align-items: baseline;
  gap: 0.3em;
  flex-wrap: wrap;
`;

const StyledWoltLogo = styled(WoltLogo)`
  height: 0.85em;
`;

const StyledIconCheckmark = styled(CheckmarkCircleFilled)`
  margin-block-start: 0.25em;
  flex-shrink: 0;
  width: 12px;
  fill: ${cssThemedValue({ light: colors.pepper24, dark: colors.salt72 })};
`;

const StyledAgreementWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin: 0;
  padding: 0;
  align-items: flex-start;

  &:not(:last-of-type) {
    margin-block-end: 1.14em;
  }
`;

export const AgreementsDetails = ({
  author,
  agreements,
  isCreatedByWolt,
  isLoading,
  ...props
}: AgreementsDetailsProps) => {
  const campaignCreator = useMemo(() => {
    const firstName = author?.name.first_name;
    const lastName = isCreatedByWolt ? `${author?.name.last_name?.charAt(0)}.` : author?.name.last_name;

    return (
      <>
        {firstName} {lastName} {isCreatedByWolt && <StyledWoltLogo />}
      </>
    );
  }, [isCreatedByWolt, author]);

  return (
    <CampaignFormItem
      data-test-id={props['data-test-id']}
      value={
        <>
          {isLoading ? (
            <Spinner data-test-id={TestIds.DETAILS.AGREEMENTS.SPINNER} size="medium" />
          ) : (
            <StyledCreatorWrapper>
              <FormattedMessage
                id="campaigns.creator"
                values={{
                  user: <StyledCreator>{campaignCreator}</StyledCreator>,
                }}
              />
            </StyledCreatorWrapper>
          )}

          {agreements.map((agreement, idx) => (
            <StyledAgreementWrapper key={idx} data-test-id={TestIds.DETAILS.AGREEMENTS.AGREEMENT}>
              <StyledIconCheckmark />
              <Typography.Body3>{agreement}</Typography.Body3>
            </StyledAgreementWrapper>
          ))}
        </>
      }
    />
  );
};
